import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { Toaster } from 'app/shared/lib/toaster';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CaseService } from 'app/cm/services/case-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { EventAggregator } from 'aurelia-event-aggregator';
import { MenuRefreshEvent } from 'app/shared/lib/events';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { localSort } from 'app/shared/lib/sorting';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { deepClone } from 'app/shared/lib/utilities';
import { EmailNotificationsRecipients } from 'app/core/components/email-notifications/email-notifications-recipients';
import { EmailNotificationsSelected } from 'app/core/components/email-notifications/email-notifications-selected';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class ManageCaseQueuesPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected validateForm = validate;
    protected queueFormIsValid: boolean = false;
    protected emailsFormIsValid: boolean = false;
    protected caseQueues: ReadonlyArray<CM.CaseQueue> | null = null;

    private apiSorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    private localSortProperties: string[] = ['queueStatusValue'];
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;
    protected caseQueueDetails: CM.CaseQueue | null = null;

    ////****************** Element References ***************************************////
    private $queueModal!: IPopup;
    protected $queueForm!: IForm;
    private $emailsModal!: IPopup;
    protected $emailsForm!: IForm;
    protected $emailNotificationsRecipients!: EmailNotificationsRecipients;
    protected $emailNotificationsSelected!: EmailNotificationsSelected;
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly caseService: CaseService,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(): Promise<void> {
        this.reload();
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadCaseQueues();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const { localSorting } = this;
            const caseQueues: CM.CaseQueue[] = <CM.CaseQueue[]>this.caseQueues;
            if(direction === null) {
                this.localSorting.remove(property);
            } else {
                this.localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(caseQueues, localSorting);
        }
        // Else If API Sort
        else {
            const { apiSorting } = this;
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }

    ////****************** Functions ************************************************////
    protected refreshMainMenu(): void {
        this.events.publish(new MenuRefreshEvent);
        this.accountContext.clear();
    }

    protected addQueue(): void {
        this.$queueModal.open();
        this.caseQueueDetails = null;
    }

    protected editCaseQueue(caseQueue: CM.CaseQueue) {
        if(!caseQueue || !this.$queueModal) {
            return;
        }
        this.caseQueueDetails = deepClone(caseQueue);
        this.$queueModal.open();
    }

    protected closeQueueModal(): void {
        if(this.$queueForm.reset) {
            this.$queueForm.reset();
        }
        this.$queueModal.close();
    }

    protected handleQueueFormSuccess({ detail }: FormSuccessCustomEvent): void {
        this.$queueModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        this.refreshMainMenu();
        this.reload();
    }

    //#region Edit Email Recipients Modal
    protected async handleEmailsFormSuccess(): Promise<void> {
        // reload information in both email notifications components
        if(this.$emailNotificationsRecipients) await this.$emailNotificationsRecipients.reload();
        if(this.$emailNotificationsSelected) {
            this.$emailNotificationsSelected.reset(); // reset selections
            await this.$emailNotificationsSelected.reloadInformation(true); // reload users as well
        }
        await this.reload();
    }

    protected editEmailNotifications(caseQueue: CM.CaseQueue) {
        if(!caseQueue || !this.$emailsModal) return;
        this.caseQueueDetails = deepClone(caseQueue);
        this.$emailsModal.open();
    }

    protected async addRecipients() {
        await this.$emailsForm.submit();
    }

    //#endregion Edit Email Recipients Modal

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadCaseQueues(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.apiSorting,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.caseService.loadQueues(options);
            this.caseQueues = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async deactivateQueue(caseQueue: CM.CaseQueue): Promise<void> {
        if(!caseQueue.id) return;

        const result = await confirmation.showDeactivate('cm:queue.$long', 1, false);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }

        if(!(result.dialogResponse === confirmation.options.OK)) {
            return;
        }

        try {
            this.state.beginSave();
            const response = await this.caseService.activateQueue(caseQueue.id, false);

            if(response.isSuccessful) {
                this.refreshMainMenu();
                this.reload();
            }
            this.toaster.standard.activate('cm:queue.$long', false, response.isSuccessful);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async reactivateQueue(caseQueue: CM.CaseQueue): Promise<void> {
        if(!caseQueue.id) return;

        try {
            this.state.beginSave();
            const response = await this.caseService.activateQueue(caseQueue.id, true);

            if(response.isSuccessful) {
                this.refreshMainMenu();
                this.reload();
            }
            this.toaster.standard.activate('cm:queue.$long', true, response.isSuccessful);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
